import type { BoxTruckAssetProfilePatchInput, BoxTruckManualAssetDetailsInput, Truck, TruckDraft } from '@numeo/types-core'

const ASSET_PROFILE_FIELDS = [
    'vehicleType',
    'expirationDate',
    'insuranceExpirationDate',
    'licenseExpirationDate',
    'payloadLbs',
    'gvwLbs',
    'registrationPlate',
    'registrationState',
    'insuranceCompany',
    'insurancePolicyNumber',
    'cargoLengthIn',
    'cargoWidthIn',
    'cargoHeightIn',
    'doorWidthIn',
    'doorHeightIn',
    'dockHeightRamps',
    'equipmentCapabilities',
    'team',
] as const satisfies readonly (keyof BoxTruckManualAssetDetailsInput)[]

export type BoxTruckEquipmentEditableSnapshot = {
    root: Pick<Truck, 'unitNumber' | 'vin' | 'make' | 'model' | 'year'>
    boxTruck: BoxTruckManualAssetDetailsInput
}

export interface ResumableCreation<T> {
    current: T | null
}

export type TruckCreationSession = symbol

export const createTruckCreationSession = (): TruckCreationSession => Symbol('truck-creation-session')

export const ensureCreatedOnce = async <T>(state: ResumableCreation<T>, create: () => Promise<T>): Promise<T> => {
    if (state.current) return state.current
    const created = await create()
    state.current = created
    return created
}

export const hasRequiredBoxTruckVehicleType = (truck: Pick<Truck, 'boxTruck'> | null | undefined): boolean => Boolean(truck?.boxTruck?.vehicleType?.trim())

// Takes a DRAFT as well as a saved vehicle: the claim form snapshots one before it has an id or a complete
// Box Truck block, and a saved `Truck` is assignable to it.
/**
 * The Box Truck details of a vehicle that has none — every editable field explicitly absent.
 *
 * The baseline a CREATE compares against. Built from the same field list the snapshot uses, so the two
 * cannot disagree about what "nothing set" looks like, and so no caller has to fabricate an empty vehicle
 * to obtain one.
 */
export const emptyBoxTruckDetails = (): BoxTruckManualAssetDetailsInput =>
    Object.fromEntries(ASSET_PROFILE_FIELDS.map((field) => [field, null])) as BoxTruckManualAssetDetailsInput

export const boxTruckEquipmentSnapshot = (truck: TruckDraft | null | undefined): BoxTruckEquipmentEditableSnapshot | null => {
    if (!truck) return null
    const boxTruck = Object.fromEntries(ASSET_PROFILE_FIELDS.map((field) => [field, truck.boxTruck?.[field] ?? null])) as BoxTruckManualAssetDetailsInput
    return {
        root: {
            unitNumber: truck.unitNumber ?? '',
            vin: truck.vin ?? '',
            make: truck.make ?? '',
            model: truck.model ?? '',
            year: truck.year ?? '',
        },
        boxTruck,
    }
}

/**
 * The asset fields the SERVER stores deduplicated and sorted (`stringArray` in ASSET_FIELD_TYPES).
 *
 * Only these may be compared order-insensitively. Naming them here rather than inferring "it is an
 * array" is the point: `buildOptimisticPatch` is also the driver-profile patch builder, and the first
 * array field added there would otherwise inherit "reordering is not a change" against a server that
 * compares by order — where the symptom is a silently LOST write, not a 409 anyone would notice.
 */
const SERVER_SORTED_ASSET_FIELDS: ReadonlySet<string> = new Set(['equipmentCapabilities'])

/**
 * Whether a field is unchanged, for the one field kind `Object.is` cannot answer for: an array.
 *
 * The forms deep-clone the selected vehicle, so an untouched array arrives as a NEW reference and
 * reference equality calls it modified — every unrelated edit would then resend the list and carry an
 * expected value that can lose a race it had no business entering. Arrays are therefore always
 * compared by CONTENT, which is correct whatever the server does with order. Ignoring order on top of
 * that is only correct where the server sorts, so it is opt-in per field. Absent and empty are the
 * same answer — "none" — so selecting a value and removing it again is not a write.
 */
const unchangedValue = (a: unknown, b: unknown, ignoreOrder: boolean): boolean => {
    // Array semantics apply only when one side actually IS an array — every other field keeps the
    // reference/identity comparison it has always had, `undefined` vs `null` included.
    if (!Array.isArray(a) && !Array.isArray(b)) return Object.is(a, b)
    const aList = Array.isArray(a) ? a : a == null ? [] : null
    const bList = Array.isArray(b) ? b : b == null ? [] : null
    if (!aList || !bList) return Object.is(a, b)
    if (aList.length !== bList.length) return false
    const left = aList.map(String)
    const right = bList.map(String)
    if (!ignoreOrder) return left.every((entry, index) => entry === right[index])
    const sortedRight = [...right].sort()
    return [...left].sort().every((entry, index) => entry === sortedRight[index])
}

export const buildOptimisticPatch = <T extends object>(current: T, initial: T, orderInsensitiveFields: ReadonlySet<string> = new Set()) => {
    const changedEntries = (Object.keys(current) as Array<keyof T>)
        .filter((field) => !unchangedValue(current[field], initial[field], orderInsensitiveFields.has(String(field))))
        .map((field) => [field, current[field]] as const)
    return {
        changes: Object.fromEntries(changedEntries) as Partial<T>,
        expected: Object.fromEntries(changedEntries.map(([field]) => [field, initial[field] ?? null])) as { [K in keyof T]?: T[K] | null },
    }
}

interface SaveBoxTruckEquipmentChangesParams {
    truckId: string
    current: BoxTruckEquipmentEditableSnapshot
    initial: BoxTruckEquipmentEditableSnapshot
    updateAsset: (truckId: string, patch: BoxTruckAssetProfilePatchInput) => Promise<void>
    updateRoot: (truckId: string, changes: Partial<Truck>) => Promise<unknown>
    onBaselineAdvanced: (snapshot: BoxTruckEquipmentEditableSnapshot) => void
    forceAssetUpdate?: boolean
    forceRootUpdate?: boolean
}

export const saveBoxTruckEquipmentChanges = async ({
    truckId,
    current,
    initial,
    updateAsset,
    updateRoot,
    onBaselineAdvanced,
    forceAssetUpdate = false,
    forceRootUpdate = false,
}: SaveBoxTruckEquipmentChangesParams): Promise<void> => {
    let baseline = initial
    const boxTruckPatch = buildOptimisticPatch(current.boxTruck, baseline.boxTruck, SERVER_SORTED_ASSET_FIELDS) as BoxTruckAssetProfilePatchInput
    if (forceAssetUpdate || Object.keys(boxTruckPatch.changes).length > 0) {
        await updateAsset(truckId, boxTruckPatch)
        baseline = { ...baseline, boxTruck: current.boxTruck }
        onBaselineAdvanced(baseline)
    }

    const rootPatch = buildOptimisticPatch(current.root, baseline.root)
    if (forceRootUpdate || Object.keys(rootPatch.changes).length > 0) {
        await updateRoot(truckId, rootPatch.changes)
        baseline = { ...baseline, root: current.root }
        onBaselineAdvanced(baseline)
    }
}
